"use client";

/**
 * ThemeService - Service for managing theme customization
 * 
 * This service handles interactions with Firestore to store, retrieve,
 * and manage theme configurations.
 */

import { collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/clientApp';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// Check if code is running in browser environment
const isBrowser = typeof window !== 'undefined';

// Collection name for themes in Firestore
const THEMES_COLLECTION = 'themes';

// Default theme configuration
export const DEFAULT_THEME = {
  primaryColor: '#1e3a8a', // orthodox-blue-800
  secondaryColor: '#f3c677', // orthodox-gold-400
  bgColor: '#ffffff',
  textColor: '#111827',
  headingColor: '#1e3a8a',
  fontPrimary: 'Inter, sans-serif',
  fontSecondary: 'Playfair Display, serif',
  borderRadius: '0.375rem',
  containerMaxWidth: '80rem',
  navbarHeight: '4rem',
  backgroundImage: 'https://i.ibb.co/DGc4bWW/church-header.jpg', // Default background image
  name: 'Default',
  id: 'default',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// Theme interface
export interface ThemeConfig {
  id?: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
  bgColor: string;
  textColor: string;
  headingColor: string;
  fontPrimary: string;
  fontSecondary: string;
  borderRadius: string;
  containerMaxWidth: string;
  navbarHeight: string;
  backgroundImage: string; // URL for background image
  createdAt: string;
  updatedAt: string;
}

/**
 * Gets the active theme configuration
 * @returns Promise with the active theme configuration
 */
export async function getActiveTheme(): Promise<ThemeConfig> {
  try {
    // Check for active theme in localStorage (only in browser environment)
    let activeThemeId: string | null = null;
    
    if (isBrowser) {
      activeThemeId = localStorage.getItem('activeThemeId');
      
      if (activeThemeId) {
        const theme = await getThemeById(activeThemeId);
        
        if (theme) {
          return theme;
        }
      }
    }
    
    // If no active theme or not found, get the most recently updated theme
    const q = query(
      collection(db, THEMES_COLLECTION),
      orderBy('updatedAt', 'desc'),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const themeDoc = querySnapshot.docs[0];
      const theme = {
        id: themeDoc.id,
        ...themeDoc.data(),
      } as ThemeConfig;
      
      // Set as active theme (only in browser environment)
      if (isBrowser) {
        localStorage.setItem('activeThemeId', theme.id as string);
      }
      
      return theme;
    }
    
    // If no themes in database, return default theme
    return DEFAULT_THEME;
  } catch (error) {
    console.error('Error getting active theme:', error);
    return DEFAULT_THEME;
  }
}

/**
 * Gets a theme by ID
 * @param id The theme ID
 * @returns Promise with the theme configuration or null if not found
 */
export async function getThemeById(id: string): Promise<ThemeConfig | null> {
  try {
    const docRef = doc(db, THEMES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as ThemeConfig;
    }
    
    return null;
  } catch (error) {
    console.error(`Error getting theme by ID (${id}):`, error);
    return null;
  }
}

/**
 * Gets all available themes
 * @returns Promise with an array of theme configurations
 */
export async function getAllThemes(): Promise<ThemeConfig[]> {
  try {
    const q = query(
      collection(db, THEMES_COLLECTION),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const themes: ThemeConfig[] = [];
    
    querySnapshot.forEach((doc) => {
      themes.push({
        id: doc.id,
        ...doc.data(),
      } as ThemeConfig);
    });
    
    // Add default theme if not already in the list
    const hasDefaultTheme = themes.some((theme) => theme.id === 'default');
    
    if (!hasDefaultTheme) {
      themes.push(DEFAULT_THEME);
    }
    
    return themes;
  } catch (error) {
    console.error('Error getting all themes:', error);
    return [DEFAULT_THEME];
  }
}

/**
 * Creates a new theme
 * @param theme The theme configuration
 * @returns Promise with the created theme ID
 */
export async function createTheme(theme: Omit<ThemeConfig, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  try {
    const themeWithTimestamps = {
      ...theme,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    const docRef = await addDoc(collection(db, THEMES_COLLECTION), themeWithTimestamps);
    return docRef.id;
  } catch (error) {
    console.error('Error creating theme:', error);
    throw error;
  }
}

/**
 * Updates an existing theme
 * @param id The theme ID
 * @param theme The updated theme configuration
 * @returns Promise indicating success
 */
export async function updateTheme(id: string, theme: Partial<ThemeConfig>): Promise<void> {
  try {
    const themeWithTimestamp = {
      ...theme,
      updatedAt: new Date().toISOString(),
    };
    
    await updateDoc(doc(db, THEMES_COLLECTION, id), themeWithTimestamp);
  } catch (error) {
    console.error(`Error updating theme (${id}):`, error);
    throw error;
  }
}

/**
 * Sets a theme as active
 * @param id The theme ID to set as active
 * @returns Promise with the active theme configuration
 */
export async function setActiveTheme(id: string): Promise<ThemeConfig> {
  try {
    const theme = await getThemeById(id);
    
    if (!theme) {
      throw new Error(`Theme with ID ${id} not found`);
    }
    
    // Store active theme ID in localStorage (only in browser environment)
    if (isBrowser) {
      localStorage.setItem('activeThemeId', id);
    }
    
    return theme;
  } catch (error) {
    console.error(`Error setting active theme (${id}):`, error);
    throw error;
  }
}

/**
 * Applies a theme to the document
 * @param theme The theme configuration to apply
 */
export function applyTheme(theme: ThemeConfig): void {
  // Only run in browser environment
  if (!isBrowser) return;
  
  try {
    // Apply CSS variables to document root
    const root = document.documentElement;
    
    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--secondary-color', theme.secondaryColor);
    root.style.setProperty('--bg-color', theme.bgColor);
    root.style.setProperty('--text-color', theme.textColor);
    root.style.setProperty('--heading-color', theme.headingColor);
    root.style.setProperty('--font-primary', theme.fontPrimary);
    root.style.setProperty('--font-secondary', theme.fontSecondary);
    root.style.setProperty('--border-radius', theme.borderRadius);
    root.style.setProperty('--container-max-width', theme.containerMaxWidth);
    root.style.setProperty('--navbar-height', theme.navbarHeight);
    root.style.setProperty('--background-image', `url('${theme.backgroundImage}')`);
  } catch (error) {
    console.error('Error applying theme:', error);
  }
}

/**
 * Imports a theme from a JSON file
 * @param file The JSON file containing the theme configuration
 * @returns Promise with the imported theme configuration
 */
export async function importThemeFromFile(file: File): Promise<ThemeConfig> {
  // Verify browser environment
  if (!isBrowser) {
    return Promise.reject(new Error('Import theme is only available in browser environment'));
  }
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const theme = JSON.parse(content);
        
        // Validate theme configuration
        const requiredProps = [
          'name', 'primaryColor', 'secondaryColor', 'bgColor',
          'textColor', 'headingColor', 'fontPrimary', 'fontSecondary',
        ];
        
        for (const prop of requiredProps) {
          if (!theme[prop]) {
            reject(new Error(`Invalid theme configuration: missing ${prop}`));
            return;
          }
        }
        
        // Add timestamps if not present
        if (!theme.createdAt) {
          theme.createdAt = new Date().toISOString();
        }
        
        theme.updatedAt = new Date().toISOString();
        
        resolve(theme as ThemeConfig);
      } catch (error) {
        reject(new Error('Invalid JSON file'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsText(file);
  });
}

/**
 * Exports a theme to a JSON file and initiates download
 * @param theme The theme configuration to export
 */
export function exportTheme(theme: ThemeConfig): void {
  // Only run in browser environment
  if (!isBrowser) {
    console.error('Export theme is only available in browser environment');
    return;
  }
  
  try {
    // Convert to JSON string
    const jsonString = JSON.stringify(theme, null, 2);
    
    // Create Blob
    const blob = new Blob([jsonString], { type: 'application/json' });
    
    // Create object URL
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and click it to download
    const link = document.createElement('a');
    link.href = url;
    link.download = `theme-${theme.name.toLowerCase().replace(/\s+/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error exporting theme:', error);
    throw error;
  }
}

/**
 * Uploads a theme file to Google Drive
 * @param file The theme file to upload
 * @returns Promise with the upload result
 */
export async function uploadThemeToStorage(file: File): Promise<string> {
  try {
    // Upload to Firebase Storage
    const storage = getStorage();
    const fileRef = storageRef(storage, `themes/${file.name}`);
    
    await uploadBytes(fileRef, file);
    const downloadUrl = await getDownloadURL(fileRef);
    
    return downloadUrl;
  } catch (error) {
    console.error('Error uploading theme to storage', error);
    throw error;
  }
}